/**
 * Tiles, ports and rotation. Transcribed from docs/02 §2.11 (the paste-ready
 * port table) and §2.1 (conventions).
 *
 * Sides are N E S W, clockwise. rotate(side, rot) = SIDES[(i + rot/90) % 4].
 * All port tables below are at rotation 0.
 */

export const SIDES = ['N', 'E', 'S', 'W'] as const;
export type Side = (typeof SIDES)[number];

export type PortKind = 'in' | 'out' | 'both';

export type TileId =
  | 'vat'
  | 'bucket'
  | 'wall'
  | 'pipe_straight'
  | 'pipe_corner'
  | 'pipe_cross'
  | 'mixer'
  | 'splitter';

export type Rotation = 0 | 90 | 180 | 270;

export function sideIndex(s: Side): number {
  return SIDES.indexOf(s);
}

export function rotateSide(s: Side, rot: Rotation): Side {
  return SIDES[(sideIndex(s) + rot / 90) % 4]!;
}

/** The side you enter through when leaving a neighbour via `s`. */
export function opposite(s: Side): Side {
  return SIDES[(sideIndex(s) + 2) % 4]!;
}

export function delta(s: Side): { dx: number; dy: number } {
  switch (s) {
    case 'N':
      return { dx: 0, dy: -1 };
    case 'E':
      return { dx: 1, dy: 0 };
    case 'S':
      return { dx: 0, dy: 1 };
    case 'W':
      return { dx: -1, dy: 0 };
  }
}

/** Ports at rotation 0 (docs/02 2.11). */
const PORTS_R0: Record<TileId, Partial<Record<Side, PortKind>>> = {
  vat: { S: 'out' },
  bucket: { N: 'in' },
  wall: {},
  pipe_straight: { N: 'both', S: 'both' },
  pipe_corner: { N: 'both', E: 'both' },
  pipe_cross: { N: 'both', S: 'both', E: 'both', W: 'both' },
  // A mixer has ONE spout and accepts paint from every other side.
  //
  // It used to have exactly two inlets, W and E, always opposite each other.
  // That rejected boards which were chemically and quantitatively correct -
  // e.g. red arriving from the east and magenta from the south to make rose -
  // purely because the two feeds met at a right angle. Players reasonably read
  // a mixer as "pipes in, one spout out"; the opposite-inlets rule was a
  // hidden constraint that made correct thinking fail. The spout direction
  // remains the real design lever: you must still aim the output.
  mixer: { N: 'in', E: 'in', W: 'in', S: 'out' },
  splitter: { N: 'in', E: 'out', W: 'out' },
};

/** Which rotations a solver must enumerate (docs/02 2.11 rotation_classes). */
export const ROTATION_CLASSES: Record<TileId, Rotation[]> = {
  vat: [0, 90, 180, 270],
  bucket: [0, 90, 180, 270],
  wall: [0],
  pipe_straight: [0, 90],
  pipe_corner: [0, 90, 180, 270],
  pipe_cross: [0],
  mixer: [0, 90, 180, 270],
  splitter: [0, 90, 180, 270],
};

export function portKind(tile: TileId, rot: Rotation, side: Side): PortKind | undefined {
  // A port declared on side s at rot 0 sits on rotate(s, rot) after rotation,
  // so to ask about `side` we rotate backwards.
  const base = rotateSide(side, ((360 - rot) % 360) as Rotation);
  return PORTS_R0[tile][base];
}

export function canEnter(tile: TileId, rot: Rotation, side: Side): boolean {
  const k = portKind(tile, rot, side);
  return k === 'in' || k === 'both';
}

export function canExit(tile: TileId, rot: Rotation, side: Side): boolean {
  const k = portKind(tile, rot, side);
  return k === 'out' || k === 'both';
}

/**
 * For pipes: given the side a blob entered through, the side it leaves by.
 * Undefined for tiles whose routing is decided in RESOLVE (mixer, splitter,
 * bucket) or that never route (vat, wall).
 */
export function pairedPort(tile: TileId, rot: Rotation, entry: Side): Side | undefined {
  const base = rotateSide(entry, ((360 - rot) % 360) as Rotation);
  let pairedBase: Side | undefined;
  switch (tile) {
    case 'pipe_straight':
      // N<->S only; E/W are not ports at rot 0.
      if (base === 'N') pairedBase = 'S';
      else if (base === 'S') pairedBase = 'N';
      break;
    case 'pipe_corner':
      if (base === 'N') pairedBase = 'E';
      else if (base === 'E') pairedBase = 'N';
      break;
    case 'pipe_cross':
      // Two independent lanes: N<->S and E<->W.
      pairedBase = opposite(base);
      break;
    default:
      return undefined;
  }
  return pairedBase === undefined ? undefined : rotateSide(pairedBase, rot);
}

/** Which lane of a cross a blob is on. Used for the capacity-2 rule. */
export function crossLane(entry: Side): 'ns' | 'ew' {
  return entry === 'N' || entry === 'S' ? 'ns' : 'ew';
}

export const DRAGGABLE_TILES: TileId[] = [
  'pipe_straight',
  'pipe_corner',
  'pipe_cross',
  'mixer',
  'splitter',
];
